import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useNavigate } from 'react-router-dom';

interface ComparisonFeature {
  feature: string;
  hipobuy: string;
  competitor: string;
}

interface ProCon {
  label: string;
  description: string;
}

interface FAQItem {
  q: string;
  a: string;
}

export interface ComparisonData {
  competitorName: string;
  competitorSlug: string;
  competitorFullName: string;
  heroImageUrl: string;
  h1Line1: string;
  h1Line2: string;
  introParagraphs: string[];
  features: ComparisonFeature[];
  hipobuyPros: ProCon[];
  competitorPros: ProCon[];
  hipobuyCons: ProCon[];
  competitorCons: ProCon[];
  verdictTitle: string;
  verdictParagraphs: string[];
  faqs: FAQItem[];
}

function buildArticleSchema(data: ComparisonData, siteUrl: string, pageSlug: string) {
  const pageUrl = `${siteUrl}/${pageSlug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `Hipobuy Spreadsheet vs ${data.competitorFullName} — Full Comparison 2026`,
    description: data.introParagraphs[0],
    author: {
      '@type': 'Person',
      name: 'Marco Chen',
    },
    datePublished: '2026-07-07',
    dateModified: '2026-07-09',
    publisher: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet Guide',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    image: data.heroImageUrl,
    inLanguage: 'en',
    wordCount: 2200,
  };
}

function buildFAQSchema(data: ComparisonData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

export default function ComparisonTemplate({ data }: { data: ComparisonData }) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hipobuyspread.site';
  const pageSlug = `hipobuy-vs-${data.competitorSlug}`;
  const navigate = useNavigate();
  const {
    competitorName,
    competitorSlug,
    competitorFullName,
    heroImageUrl,
    h1Line1,
    h1Line2,
    introParagraphs,
    features,
    hipobuyPros,
    competitorPros,
    hipobuyCons,
    competitorCons,
    verdictTitle,
    verdictParagraphs,
    faqs,
  } = data;

  return (
    <main>
      <JsonLd data={buildArticleSchema(data, siteUrl, pageSlug)} />
      <JsonLd data={buildFAQSchema(data)} />
      <Navbar />

      <article className="pt-20 md:pt-28">
        {/* Hero */}
        <section className="relative w-full px-6 md:px-10 py-16 md:py-24 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src={heroImageUrl}
              alt={`Hipobuy vs ${competitorName} comparison`}
              className="w-full h-full object-cover object-top"
              title={`Hipobuy Spreadsheet vs ${competitorFullName}`}
            />
          </div>
          <div className="relative max-w-4xl mx-auto z-10">
            <div className="flex items-center gap-2 text-xs font-medium text-foreground-400 uppercase tracking-wider mb-6">
              <button
                onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="hover:text-accent-600 transition-colors duration-200 cursor-pointer"
              >
                Home
              </button>
              <span>/</span>
              <span className="text-accent-600">Comparisons</span>
            </div>
            <h1 className="font-heading text-4xl md:text-7xl font-extrabold text-foreground-800 leading-none tracking-tight mb-6">
              {h1Line1}
              <br />
              <span className="text-accent-600">{h1Line2}</span>
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl">
              {introParagraphs[0]}
            </p>
            <div className="flex items-center gap-4 mt-8">
              <span className="text-xs text-foreground-400">By Marco Chen</span>
              <span className="text-xs text-foreground-300">|</span>
              <span className="text-xs text-foreground-400">July 7, 2026</span>
              <span className="text-xs text-foreground-300">|</span>
              <span className="text-xs text-foreground-400">10 min read</span>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="relative w-full px-6 md:px-10 py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="prose-custom">
              {/* Intro */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-5">
                Understanding the Two Platforms
              </h2>
              {introParagraphs.slice(1).map((p, i) => (
                <p key={i} className="text-foreground-600 leading-relaxed mb-6">{p}</p>
              ))}

              {/* Comparison Table */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-5 mt-14">
                Head-to-Head Feature Comparison
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Here is a detailed, side-by-side breakdown of how <strong className="text-foreground-800">Hipobuy Spreadsheet</strong> stacks up against <strong className="text-foreground-800">{competitorFullName}</strong> across the features that matter most to shoppers.
              </p>

              <div className="overflow-x-auto mb-8 rounded-lg border border-background-200/60">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left p-4 font-semibold text-foreground-800">Feature</th>
                      <th className="text-left p-4 font-semibold text-primary-600">Hipobuy Spreadsheet</th>
                      <th className="text-left p-4 font-semibold text-foreground-700">{competitorName} Spreadsheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((row, i) => (
                      <tr key={i} className="border-t border-background-200/60 hover:bg-background-100/50 transition-colors">
                        <td className="p-4 font-medium text-foreground-800">{row.feature}</td>
                        <td className="p-4 text-foreground-600">{row.hipobuy}</td>
                        <td className="p-4 text-foreground-600">{row.competitor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pros & Cons */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-5 mt-14">
                Pros &amp; Cons at a Glance
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Hipobuy */}
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/60">
                  <h3 className="font-heading font-bold text-lg text-primary-600 mb-4">Hipobuy Spreadsheet — Pros</h3>
                  <ul className="space-y-2.5">
                    {hipobuyPros.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-foreground-600">
                        <span className="text-accent-500 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-sm"></i>
                        </span>
                        <span><strong className="text-foreground-800">{item.label}:</strong> {item.description}</span>
                      </li>
                    ))}
                  </ul>
                  <h3 className="font-heading font-bold text-lg text-foreground-500 mt-5 mb-3">Hipobuy Spreadsheet — Cons</h3>
                  <ul className="space-y-2.5">
                    {hipobuyCons.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-foreground-500">
                        <span className="text-foreground-300 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line text-sm"></i>
                        </span>
                        <span><strong className="text-foreground-700">{item.label}:</strong> {item.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Competitor */}
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/60">
                  <h3 className="font-heading font-bold text-lg text-foreground-700 mb-4">{competitorName} Spreadsheet — Pros</h3>
                  <ul className="space-y-2.5">
                    {competitorPros.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-foreground-600">
                        <span className="text-accent-500 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-sm"></i>
                        </span>
                        <span><strong className="text-foreground-800">{item.label}:</strong> {item.description}</span>
                      </li>
                    ))}
                  </ul>
                  <h3 className="font-heading font-bold text-lg text-foreground-500 mt-5 mb-3">{competitorName} Spreadsheet — Cons</h3>
                  <ul className="space-y-2.5">
                    {competitorCons.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-foreground-500">
                        <span className="text-foreground-300 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line text-sm"></i>
                        </span>
                        <span><strong className="text-foreground-700">{item.label}:</strong> {item.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Verdict */}
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-5 mt-14">
                {verdictTitle}
              </h2>
              {verdictParagraphs.map((p, i) => (
                <p key={i} className="text-foreground-600 leading-relaxed mb-6">{p}</p>
              ))}

              {/* FAQ */}
              <div className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-8">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {faqs.map((faq, i) => (
                    <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/60">
                      <h3 className="font-heading font-semibold text-foreground-800 mb-2">{faq.q}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Comparisons */}
              <div className="mt-14 p-6 rounded-xl bg-background-100 border border-background-200/60">
                <h3 className="font-heading font-semibold text-foreground-800 mb-4">Explore More Comparisons</h3>
                <div className="flex flex-wrap gap-2">
                  {['Kakobuy', 'CSSBuy', 'Superbuy', 'Oopbuy'].filter(c => c !== competitorName).map((c) => (
                    <button
                      key={c}
                      onClick={() => { navigate(`/hipobuy-vs-${c.toLowerCase()}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-background-50 border border-background-200/60 text-foreground-600 hover:border-accent-300 hover:text-accent-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Hipobuy vs {c}
                      <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-14 p-8 md:p-10 rounded-2xl bg-accent-50 border border-accent-200 text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
                  Ready to Explore Hipobuy Spreadsheet?
                </h2>
                <p className="text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
                  See for yourself why thousands of shoppers choose Hipobuy for organized product discovery. Browse curated categories or read our in-depth guides.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Browse hipobuy sheet
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                  <button
                    onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border-2 border-accent-300 text-accent-700 hover:bg-accent-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Back to Home
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              </div>

              {/* Internal Link Back */}
              <div className="mt-14 pt-10 border-t border-background-200/60 text-center">
                <p className="text-sm text-foreground-400 mb-3">Looking for more shopping resources?</p>
                <button
                  onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                  Return to Hipobuy Spreadsheet Homepage
                </button>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}